import { useMsal } from "@azure/msal-react";
import { useCallback, useMemo, useRef, useEffect } from "react";
import { useTenant } from "../contexts/tenant-context";
import { extractApiErrorMessage } from "../utils/apiErrorMessage";

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Types
export interface ApiResponse<T = any> {
  data: T;
  success: boolean;
  message?: string;
}

export interface ApiError {
  message: string;
  status: number;
  details?: any;
}

export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

export interface ApiCallOptions {
  method: HttpMethod;
  endpoint: string;
  payload?: any;
  headers?: Record<string, string>;
  requireAuth?: boolean;
}

export interface BlobApiResponse {
  blob: Blob;
  contentType: string;
  fileName?: string;
}

// Custom hook for API calls with authentication
export const useApiService = () => {
  const { instance, accounts } = useMsal();
  const { selectedTenantId } = useTenant();
  
  // Use ref to avoid recreating callbacks when accounts array reference changes
  const accountsRef = useRef(accounts);
  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  const getCurrentAccount = useCallback(() => {
    const activeAccount = instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const currentAccounts = accountsRef.current;
    const storedHomeAccountId = localStorage.getItem("pontis_active_account_home_id");

    if (storedHomeAccountId) {
      const matchedAccount = currentAccounts.find(
        (account) => account.homeAccountId === storedHomeAccountId
      );
      if (matchedAccount) {
        instance.setActiveAccount(matchedAccount);
        return matchedAccount;
      }
    }

    if (currentAccounts.length === 1) {
      instance.setActiveAccount(currentAccounts[0]);
      return currentAccounts[0];
    }

    return undefined;
  }, [instance]);

  const getAuthHeaders = useCallback(async (): Promise<
    Record<string, string>
  > => {
    const headers: Record<string, string> = {};
    const currentAccount = getCurrentAccount();
    if (currentAccount) {
      try {
        const response = await instance.acquireTokenSilent({
          account: currentAccount,
          scopes: [process.env.REACT_APP_MSAL_API_SCOPE || ""], // adjust scopes as needed
        });
        headers["Authorization"] = `Bearer ${response.accessToken}`;
      } catch (error) {
        // Silently handle token acquisition failure
      }
    }

    return headers;
  }, [getCurrentAccount, instance]);

  const apiCall = useCallback(
    async <T = any>({
      method,
      endpoint,
      payload,
      headers = {},
      requireAuth = true,
    }: ApiCallOptions): Promise<ApiResponse<T>> => {
      try {
        const url = `${API_BASE_URL}${
          endpoint.startsWith("/") ? endpoint : `/${endpoint}`
        }`;

        const isFormData = payload instanceof FormData;

        const authHeaders = requireAuth ? await getAuthHeaders() : {};

        const requestHeaders: Record<string, string> = {
          ...authHeaders,
          ...headers,
        };

        const hasTenantHeader = Object.keys(requestHeaders).some(
          (key) => key.toLowerCase() === "x-tenant-id"
        );

        // Add tenant context header (only if a specific tenant is selected, not Global Admin)
        // Allow explicit per-call override via headers.
        if (!hasTenantHeader && selectedTenantId && selectedTenantId !== "__GLOBAL_ADMIN__") {
          requestHeaders["X-Tenant-Id"] = selectedTenantId;
        }
        
        if (!isFormData && !requestHeaders["Content-Type"]) {
          requestHeaders["Content-Type"] = "application/json";
        }

        const config: RequestInit = {
          method,
          headers: requestHeaders,
        };

        if (payload && method !== "GET") {
          config.body = isFormData ? payload : JSON.stringify(payload);
        }
        const response = await fetch(url, config);

        if (!response.ok) {
          const responseText = await response.text();
          let errorData: unknown = null;
          if (responseText) {
            try {
              errorData = JSON.parse(responseText);
            } catch {
              errorData = { message: responseText };
            }
          }

          const apiError = new Error(
            extractApiErrorMessage(errorData, response.statusText || "Request failed")
          ) as Error & ApiError;
          apiError.status = response.status;
          apiError.details = errorData;
          throw apiError;
        }

        // Handle 204 No Content responses
        if (response.status === 204) {
          return {
            data: null as any,
            success: true,
            message: "Request successful",
          };
        }

        const data = await response.json();

        return {
          data,
          success: true,
          message: "Request successful",
        };
      } catch (error) {
        if (error && typeof error === "object" && "status" in error) {
          throw error;
        }

        const networkError = new Error(
          error instanceof Error ? error.message : "Network error occurred"
        ) as Error & ApiError;
        networkError.status = 0;
        networkError.details = error;
        throw networkError;
      }
    },
    [getAuthHeaders, selectedTenantId]
  );

  // Special method for blob responses (files, PDFs, etc.)
  const apiBlobCall = useCallback(
    async ({
      method,
      endpoint,
      payload,
      headers = {},
      requireAuth = true,
    }: ApiCallOptions): Promise<BlobApiResponse> => {
      try {
        const url = `${API_BASE_URL}${
          endpoint.startsWith("/") ? endpoint : `/${endpoint}`
        }`;

        // Detect FormData automatically
        const isFormData = payload instanceof FormData;

        // Only set JSON header if NOT FormData
        if (!isFormData) {
          headers["Content-Type"] = "application/json";
        }

        // Add auth if needed
        if (requireAuth) {
          try {
            const currentAccount = getCurrentAccount();
            if (currentAccount) {
              const response = await instance.acquireTokenSilent({
                account: currentAccount,
                scopes: [process.env.REACT_APP_MSAL_API_SCOPE || ""],
              });
              headers["Authorization"] = `Bearer ${response.accessToken}`;
            }
          } catch (err) {
            // Silently handle token acquisition failure
          }
        }

        const hasTenantHeader = Object.keys(headers).some(
          (key) => key.toLowerCase() === "x-tenant-id"
        );

        // Add tenant context header
        // Allow explicit per-call override via headers.
        if (!hasTenantHeader && selectedTenantId && selectedTenantId !== "__GLOBAL_ADMIN__") {
          headers["X-Tenant-Id"] = selectedTenantId;
        }

        const response = await fetch(url, {
          method,
          headers,
          body:
            method !== "GET"
              ? isFormData
                ? payload
                : JSON.stringify(payload)
              : undefined,
        });

        if (!response.ok) {
          const errorData = await response.text().catch(() => "Unknown error");
          const apiError = new Error(
            errorData || response.statusText
          ) as Error & ApiError;
          apiError.status = response.status;
          apiError.details = errorData;
          throw apiError;
        }

        const blob = await response.blob();
        const contentType =
          response.headers.get("content-type") || "application/octet-stream";
        const contentDisposition =
          response.headers.get("content-disposition") || "";
        const fileNameMatch = contentDisposition.match(
          /filename\*?=(?:UTF-8''|")?([^";]+)/i
        );
        
        const rawFileName = fileNameMatch?.[1]
          ? fileNameMatch[1].replace(/"/g, "").trim()
          : undefined;
        let fileName = rawFileName;
        if (rawFileName) {
          try {
            fileName = decodeURIComponent(rawFileName);
          } catch {
            fileName = rawFileName;
          }
        }

        return {
          blob,
          contentType,
          fileName,
        };
      } catch (error) {
        if (error && typeof error === "object" && "status" in error) {
          throw error;
        }

        const networkError = new Error(
          error instanceof Error ? error.message : "Network error occurred"
        ) as Error & ApiError;
        networkError.status = 0;
        networkError.details = error;
        throw networkError;
      }
    },
    [getCurrentAccount, instance, selectedTenantId]
  );

  // Convenience methods - memoized to prevent unnecessary recreations
  const apiService = useMemo(() => {
    const get = <T = any>(endpoint: string, requireAuth = true) =>
      apiCall<T>({ method: "GET", endpoint, requireAuth });

    const post = <T = any>(
      endpoint: string,
      payload?: any,
      requireAuth = true
    ) => apiCall<T>({ method: "POST", endpoint, payload, requireAuth });

    const put = <T = any>(
      endpoint: string,
      payload?: any,
      requireAuth = true
    ) => apiCall<T>({ method: "PUT", endpoint, payload, requireAuth });

    const del = <T = any>(endpoint: string, requireAuth = true) =>
      apiCall<T>({ method: "DELETE", endpoint, requireAuth });

    const patch = <T = any>(
      endpoint: string,
      payload?: any,
      requireAuth = true
    ) => apiCall<T>({ method: "PATCH", endpoint, payload, requireAuth });

    // Blob-specific methods
    const getBlob = (endpoint: string, requireAuth = true) =>
      apiBlobCall({ method: "GET", endpoint, requireAuth });

    const postBlob = (
      endpoint: string,
      payload?: any,
      requireAuth = true
    ) => apiBlobCall({ method: "POST", endpoint, payload, requireAuth });

    return {
      apiCall,
      get,
      post,
      put,
      delete: del,
      patch,
      getBlob,
      postBlob,
    };
  }, [apiCall, apiBlobCall]);

  return apiService;
};
